import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import AdminToast from '../admin/AdminToast';
import ApprenantPageShell from './ApprenantPageShell';
import ApprenantWalletHistoriqueTab from './ApprenantWalletHistoriqueTab';
import WalletActionsPanel from '../shared/WalletActionsPanel';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchWalletDashboard } from '../../services/walletService';
import { formatMoney } from '../../utils/currency';

type Dashboard = {
  solde?: number;
  devise?: string;
  summary?: { available?: number; pending?: number };
  totaux?: { creditsAcademiquesCdf?: number; bonusParrainageCdf?: number };
};

type ToastState = { message: string; variant: 'success' | 'error' } | null;
type WalletTab = 'solde' | 'historique';

export default function ApprenantWalletPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: WalletTab = searchParams.get('tab') === 'historique' ? 'historique' : 'solde';

  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const loadDashboard = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setData((await fetchWalletDashboard()) as Dashboard);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le portefeuille.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useRegisterPortalRefresh(() => void loadDashboard());

  const setTab = (tab: WalletTab) => {
    if (tab === 'historique') {
      setSearchParams({ tab: 'historique' });
      return;
    }
    setSearchParams({});
  };

  const devise = data?.devise ?? 'CDF';
  const solde = data?.summary?.available ?? data?.solde ?? 0;

  const walletTabClass = (selected: boolean) =>
    selected
      ? 'inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white'
      : 'inline-flex items-center gap-2 rounded-lg border border-outline-variant/30 bg-white px-4 py-2 text-sm font-bold text-slate-700';

  return (
    <ApprenantPageShell
      title="Mon portefeuille"
      description="Bonus, crédits académiques, paiements internes, retraits et transferts."
      sideCard={{
        label: 'Solde disponible',
        value: loading ? '…' : formatMoney(solde, devise),
        helper: data?.totaux
          ? `Crédits acad. ${formatMoney(data.totaux.creditsAcademiquesCdf ?? 0, devise)} · Bonus ${formatMoney(data.totaux.bonusParrainageCdf ?? 0, devise)}`
          : undefined,
        sideLinks: [
          { label: 'Historique complet', to: '/app/wallet?tab=historique', icon: 'receipt_long' },
          { label: 'Parrainage', to: '/app/parrainage', icon: 'groups' },
        ],
      }}
      isLoading={loading}
      isError={Boolean(error) && !data}
      errorMessage={error ?? undefined}
      onRetry={() => void loadDashboard()}
      loadingMessage="Chargement du portefeuille…"
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      {data ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="Sections portefeuille">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'solde'}
              className={walletTabClass(activeTab === 'solde')}
              onClick={() => setTab('solde')}
            >
              <LucideSymbolIcon name="payments" tone="inherit" size={18} />
              Solde
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'historique'}
              className={walletTabClass(activeTab === 'historique')}
              onClick={() => setTab('historique')}
            >
              <LucideSymbolIcon name="receipt_long" tone="inherit" size={18} />
              Historique complet
            </button>
          </div>

          {activeTab === 'solde' ? (
            <>
              <section className="grid gap-3 sm:grid-cols-3">
                <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase text-on-surface-variant">Solde</p>
                  <p className="mt-1 text-2xl font-black text-primary">{formatMoney(solde, devise)}</p>
                </article>
                <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase text-on-surface-variant">Crédits académiques</p>
                  <p className="mt-1 text-xl font-black">
                    {formatMoney(data.totaux?.creditsAcademiquesCdf ?? 0, devise)}
                  </p>
                </article>
                <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase text-on-surface-variant">Bonus parrainage</p>
                  <p className="mt-1 text-xl font-black">
                    {formatMoney(data.totaux?.bonusParrainageCdf ?? 0, devise)}
                  </p>
                </article>
              </section>

              <WalletActionsPanel
                walletSolde={solde}
                walletDevise={devise}
                showRecharge={false}
                showTransfer={false}
                onSuccess={(message) => {
                  showToast(message, 'success');
                  void loadDashboard();
                }}
                onError={(msg) => {
                  if (msg) showToast(msg, 'error');
                }}
              />
            </>
          ) : (
            <ApprenantWalletHistoriqueTab devise={devise} />
          )}
        </div>
      ) : null}
    </ApprenantPageShell>
  );
}
